/*
 * ============================================================
 * MODI — Hair ritual builder (<modi-ritual>)
 * A progressive product finder. All content (questions, routines,
 * product cards, Hair Clinic additions) is server-rendered for
 * indexability; this script only manages step state, reveals the
 * matched routine, and adds the routine to cart.
 *
 * Matching:
 *  - routine: answer to the concern question → routine block
 *    (data-concern), falling back to the first routine.
 *  - addition (Kyklos meso care): shown ONLY when the visitor
 *    answered "no" to the extensions question, because meso
 *    products are not suitable for hair with extensions. Exact
 *    concern match wins; the "any" addition is the fallback,
 *    except for the extensions concern (an extension-focused
 *    ritual never advises meso care by default).
 * Custom element → Shopify theme-editor safe.
 * ============================================================
 */
(function () {
  'use strict';
  if (customElements.get('modi-ritual')) return;

  var ANSWER_ORDER = ['hair_type', 'concern', 'extensions', 'wash'];

  class ModiRitual extends HTMLElement {
    connectedCallback() {
      this.quiz = this.querySelector('[data-ritual-quiz]');
      this.results = this.querySelector('[data-ritual-results]');
      this.summary = this.querySelector('[data-ritual-summary]');
      this.steps = Array.prototype.slice.call(this.querySelectorAll('.modi-ritual__step'));
      this.progressBar = this.querySelector('.modi-ritual__progress-bar');
      this.routines = Array.prototype.slice.call(this.querySelectorAll('.modi-ritual__routine'));
      this.additions = Array.prototype.slice.call(this.querySelectorAll('[data-addition]'));
      this.answers = {};
      this.current = 0;

      this._onClick = this._handleClick.bind(this);
      this.addEventListener('click', this._onClick);
      this._render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
    }

    _handleClick(event) {
      var option = event.target.closest('.modi-ritual__option');
      if (option && this.contains(option)) {
        this._select(option);
        return;
      }
      if (event.target.closest('[data-ritual-back]')) {
        event.preventDefault();
        this._goto(Math.max(0, this.current - 1));
        return;
      }
      if (event.target.closest('[data-ritual-restart]')) {
        event.preventDefault();
        this._restart();
        return;
      }
      var addBtn = event.target.closest('[data-ritual-addall]');
      if (addBtn) {
        event.preventDefault();
        this._addAll(addBtn);
      }
    }

    _select(option) {
      var q = option.getAttribute('data-q');
      var value = option.getAttribute('data-value');
      this.answers[q] = value;

      // visually mark selection within this step
      var step = option.closest('.modi-ritual__step');
      if (step) {
        step.querySelectorAll('.modi-ritual__option').forEach(function (o) {
          o.setAttribute('aria-pressed', o === option ? 'true' : 'false');
        });
      }

      if (this.current < this.steps.length - 1) {
        this._goto(this.current + 1);
      } else {
        this._finish();
      }
    }

    _goto(index) {
      this.current = index;
      this._render();
      // move focus to the newly shown step legend for screen readers
      var active = this.steps[index];
      if (active) {
        var legend = active.querySelector('legend, .modi-ritual__question');
        if (legend) {
          legend.setAttribute('tabindex', '-1');
          legend.focus({ preventScroll: true });
        }
      }
    }

    _render() {
      var self = this;
      this.steps.forEach(function (step, i) {
        step.hidden = i !== self.current;
      });
      if (this.progressBar) {
        // step 0 keeps a small head start so the bar never reads as dead
        var pct = Math.max(0.08, (this.current + 1) / (this.steps.length + 1));
        this.progressBar.style.transform = 'scaleX(' + pct + ')';
      }
    }

    _matchAddition(concern) {
      if (this.answers.extensions !== 'no') return null;
      var fallback = null;
      for (var i = 0; i < this.additions.length; i++) {
        var key = this.additions[i].getAttribute('data-concern');
        if (key === concern) return this.additions[i];
        if (key === 'any' && !fallback) fallback = this.additions[i];
      }
      // an extension-focused ritual never advises meso care by default
      if (concern === 'extensions') return null;
      return fallback;
    }

    _renderSummary() {
      if (!this.summary) return;
      var self = this;
      var parts = [];
      ANSWER_ORDER.forEach(function (q) {
        var pressed = self.querySelector('.modi-ritual__option[data-q="' + q + '"][aria-pressed="true"]');
        if (pressed) parts.push(pressed.textContent.trim());
      });
      if (parts.length) {
        this.summary.textContent = parts.join(' · ');
        this.summary.hidden = false;
      } else {
        this.summary.hidden = true;
      }
    }

    _finish() {
      var concern = this.answers.concern || '';
      var matched = null;
      for (var i = 0; i < this.routines.length; i++) {
        if (this.routines[i].getAttribute('data-concern') === concern) {
          matched = this.routines[i];
          break;
        }
      }
      // fall back to the first routine if no exact match
      if (!matched && this.routines.length) matched = this.routines[0];
      this.matched = matched;

      this.routines.forEach(function (r) {
        r.hidden = r !== matched;
      });

      var addition = this._matchAddition(concern);
      this.additions.forEach(function (a) {
        a.hidden = a !== addition;
      });

      this._renderSummary();

      if (this.quiz) this.quiz.hidden = true;
      if (this.results) {
        this.results.hidden = false;
        this.results.setAttribute('tabindex', '-1');
        this.results.focus({ preventScroll: true });
        this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (this.progressBar) this.progressBar.style.transform = 'scaleX(1)';
    }

    _restart() {
      this.answers = {};
      this.current = 0;
      if (this.results) this.results.hidden = true;
      if (this.quiz) this.quiz.hidden = false;
      if (this.summary) this.summary.hidden = true;
      this.additions.forEach(function (a) {
        a.hidden = true;
      });
      this.querySelectorAll('.modi-ritual__option[aria-pressed="true"]').forEach(function (o) {
        o.setAttribute('aria-pressed', 'false');
      });
      this._render();
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    _addAll(btn) {
      if (!this.matched) return;
      var nodes = this.matched.querySelectorAll('[data-variant-id]');
      var items = [];
      Array.prototype.forEach.call(nodes, function (n) {
        var id = n.getAttribute('data-variant-id');
        if (id && n.getAttribute('data-available') !== 'false') {
          items.push({ id: id, quantity: 1 });
        }
      });
      if (!items.length) return;

      var addUrl = this.getAttribute('data-cart-add-url') || '/cart/add.js';
      var cartUrl = this.getAttribute('data-cart-url') || '/cart';
      btn.setAttribute('aria-busy', 'true');
      var original = btn.textContent;
      btn.textContent = btn.getAttribute('data-adding-label') || 'Adding…';

      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error('add failed');
          return res.json();
        })
        .then(function () {
          // Try Horizon's cart drawer; otherwise go to the cart page.
          var drawer = document.getElementById('cart-drawer');
          if (drawer && typeof drawer.open === 'function') {
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            window.location.href = cartUrl;
          } else {
            window.location.href = cartUrl;
          }
        })
        .catch(function () {
          btn.removeAttribute('aria-busy');
          btn.textContent = original;
          window.location.href = cartUrl;
        });
    }
  }

  customElements.define('modi-ritual', ModiRitual);
})();
